import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiDownload, FiGithub, FiLinkedin, FiMail, FiArrowRight } from 'react-icons/fi';
import skills from '../constants/skills';
import './Home.css';
import profilePic from '../assets/images/profile.jpg';
import resumePDF from '../assets/documents/resume.pdf';

function Home() {
  const socialLinks = [
    { icon: FiGithub, href: 'https://github.com/jex23', label: 'GitHub' },
    { icon: FiLinkedin, href: 'https://linkedin.com/in/james-galos-608826215', label: 'LinkedIn' },
    { icon: FiMail, href: 'mailto:jamesgalos223@gmail.com', label: 'Email' }
  ];

  return (
    <main className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero__background">
          <div className="hero__gradient-orb hero__gradient-orb--1" />
          <div className="hero__gradient-orb hero__gradient-orb--2" />
        </div>
        
        <div className="container">
          <motion.div
            className="hero__content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
           

            <h1 className="hero__title">
              <span className="hero__greeting">Hello, I'm</span>
              <span className="hero__name">James Galos</span>
              <span className="hero__title-text">Full Stack Developer</span>
              <span className="hero__specialization">Fintech & Mobile Specialist</span>
            </h1>
            
            <motion.p 
              className="hero__subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              I architect and build scalable web and mobile applications with modern technologies.
              Currently handling <span className="hero__highlight">₱10M+ daily transactions</span> in fintech systems.
            </motion.p>

            {/* Social Links */}
            <motion.div 
              className="hero__social"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero__social-link"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.label}
                >
                  <social.icon />
                </motion.a>
              ))}
            </motion.div>
            
            <div className="hero__cta-container">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/projects" className="hero__cta hero__cta--primary">
                  <span>View My Work</span>
                  <FiArrowRight />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a 
                  href={resumePDF} 
                  download="James_Galos_Resume.pdf"
                  className="hero__cta hero__cta--secondary"
                >
                  <FiDownload />
                  <span>Download CV</span>
                </a>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="hero__image-container"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="hero__image-wrapper">
              <img
                src={profilePic}
                alt="James Galos"
                className="hero__image"
              />
              <div className="hero__image-decoration" />
              <div className="hero__image-decoration hero__image-decoration--2" />
            </div>
            
            {/* Floating Stats */}
            <motion.div 
              className="hero__stat hero__stat--1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.4 }}
            >
              <span className="hero__stat-number">3+</span>
              <span className="hero__stat-label">Years Exp</span>
            </motion.div>
            
            <motion.div 
              className="hero__stat hero__stat--2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9, duration: 0.4 }}
            >
              <span className="hero__stat-number">15+</span>
              <span className="hero__stat-label">Projects</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="section-label">Skills</span>
            <h2 className="section-title">Technical Expertise</h2>
            <p className="section-subtitle">
              Technologies I use to bring products from idea to reality
            </p>
          </motion.div>
          
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.id}
                className="skill-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true, margin: "-50px" }}
                whileHover={{ y: -5 }}
              >
                <div className="skill-glow" />
                
                <div className="skill-icon-wrapper">
                  <img src={skill.logo} alt={skill.name} className="skill-icon-img" />
                </div>

                <h3 className="skill-name">{skill.name}</h3>

                <div className="skill-level-wrapper">
                  
                  
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="section-label">Services</span>
            <h2 className="section-title">What I Bring to Your Team</h2>
            <p className="section-subtitle">
              Comprehensive solutions for modern digital challenges
            </p>
          </motion.div>

          <div className="value-grid">
            {/* Web Development */}
            <motion.div
              className="value-card"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="value-icon">
                <span>🌐</span>
              </div>
              <h3>Web Development</h3>
              <p>
                Expertise in building responsive, scalable web applications using React.js for frontend and Node.js/Express for backend. Skilled in database design (PostgreSQL, MySQL), API creation (RESTful & GraphQL), and implementing user-friendly UIs with modern CSS frameworks.
              </p>
              <div className="value-tech-stack">
                <span>React</span>
                <span>Node.js</span>
                <span>PostgreSQL</span>
              </div>
            </motion.div>

            {/* Mobile App Development */}
            <motion.div
              className="value-card"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="value-icon">
                <span>📱</span>
              </div>
              <h3>Mobile App Development</h3>
              <p>
                Proficient in cross-platform mobile development using Flutter (Dart) and native frameworks when needed. I design and implement intuitive UIs, integrate device hardware (camera, sensors), and ensure performance and maintainability across iOS and Android devices.
              </p>
              <div className="value-tech-stack">
                <span>Flutter</span>
                <span>Android</span>
                <span>iOS</span>
              </div>
            </motion.div>

            {/* Cloud & Server Hosting */}
            <motion.div
              className="value-card"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="value-icon">
                <span>☁️</span>
              </div>
              <h3>Cloud & Server Hosting</h3>
              <p>
                Hands-on experience with Linux (Ubuntu) server setup, configuring Nginx, and deploying applications on AWS EC2 and other VPS platforms. I manage secure SSH access, automate deployments with CI/CD, and monitor system performance to keep services reliable and secure.
              </p>
              <div className="value-tech-stack">
                <span>AWS</span>
                <span>Linux</span>
                <span>Docker</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta-section">
        <div className="container">
          <motion.div
            className="home-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Start Your Next Project?</h2>
            <p>Let's collaborate to build something extraordinary together</p>
            <div className="home-cta__buttons">
              <Link to="/contact" className="home-cta__btn home-cta__btn--primary">
                Get In Touch
              </Link>
              <a 
                href={resumePDF} 
                download="James_Galos_Resume.pdf"
                className="home-cta__btn home-cta__btn--secondary"
              >
                <FiDownload />
                Download Resume
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export default Home;